import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Copy, Loader2, Send, Wand2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fetchDeals, generateContent } from "@/lib/api";

const AI_BG = "https://static.prod-images.emergentagent.com/jobs/4ca59440-3756-4a33-9153-174842ff835f/images/1a713da9deb109b37ab12042e96910112909eec73e03ac7db18e18cd23cad386.png";

const TONES = ["freundlich & verkaufsstark", "seriös & informativ", "jung & frech", "luxuriös & exklusiv"];
const PLATFORMS = ["Telegram", "Instagram", "Facebook", "WhatsApp"];

export default function ContentGenerator() {
  const [deals, setDeals] = useState([]);
  const [dealId, setDealId] = useState("");
  const [tone, setTone] = useState(TONES[0]);
  const [platform, setPlatform] = useState(PLATFORMS[0]);
  const [tag, setTag] = useState("deinkanal-21");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchDeals({ min_discount: 10, sort: "score" }).then((d) => {
      setDeals(d);
      if (d.length) setDealId(d[0].id);
    });
  }, []);

  const deal = deals.find((d) => d.id === dealId);

  const handleGenerate = async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const updated = await generateContent(dealId, { tone, platform, affiliate_tag: tag });
      setResult(updated);
      toast.success("Content erfolgreich generiert");
    } catch (e) {
      toast.error("Generierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const gc = result?.generated_content;
  const fullPost = gc && result
    ? `${gc.headline}\n\n${gc.description}\n\n💶 Nur ${result.deal_price.toFixed(2)} € statt ${result.original_price.toFixed(2)} € (-${result.discount_percent}%)\n👉 ${gc.cta}\n${result.affiliate_url}\n\n${gc.hashtags.join(" ")}`
    : "";

  const copy = () => { navigator.clipboard.writeText(fullPost); toast.success("Post kopiert"); };

  return (
    <div data-testid="content-page">
      <PageHeader title="Content Studio" subtitle="KI-generierte Posts für deine Kanäle – original & verkaufsstark" />

      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Config */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl border border-black/5 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden"
        >
          <div className="relative p-6">
            <img src={AI_BG} alt="" className="absolute inset-0 w-full h-32 object-cover opacity-30 pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-[#A259FF]/10 px-3 py-1 mb-4">
                <Wand2 className="h-3.5 w-3.5 text-[#8928E2]" />
                <span className="text-xs font-semibold ai-gradient-text">KI Copywriter</span>
              </div>

              <label className="text-sm font-medium text-[#1D1D1F] block mb-2">Deal auswählen</label>
              <Select value={dealId} onValueChange={(v) => { setDealId(v); setResult(null); }}>
                <SelectTrigger className="rounded-xl bg-[#F5F5F7] border-0 mb-4" data-testid="content-deal-select">
                  <SelectValue placeholder="Deal wählen" />
                </SelectTrigger>
                <SelectContent>
                  {deals.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.product_name} · -{d.discount_percent}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-sm font-medium text-[#1D1D1F] block mb-2">Tonalität</label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger className="rounded-xl bg-[#F5F5F7] border-0" data-testid="content-tone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1D1D1F] block mb-2">Plattform</label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger className="rounded-xl bg-[#F5F5F7] border-0" data-testid="content-platform">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <label className="text-sm font-medium text-[#1D1D1F] block mb-2">Affiliate-Tag</label>
              <Input value={tag} onChange={(e) => setTag(e.target.value)} className="rounded-xl bg-[#F5F5F7] border-0 mb-5" data-testid="content-tag" />

              <Button
                onClick={handleGenerate}
                disabled={loading || !dealId}
                className="w-full rounded-full ai-gradient-bg text-white hover:opacity-90 active:scale-95 transition-all py-6"
                data-testid="content-generate-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Post generieren
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Preview */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="rounded-2xl border border-black/5 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold text-[#1D1D1F]">Vorschau · {platform}</h2>
            {gc && (
              <Button size="sm" variant="secondary" onClick={copy} className="rounded-full bg-black/5 hover:bg-black/10" data-testid="content-copy">
                <Copy className="h-3.5 w-3.5" /> Kopieren
              </Button>
            )}
          </div>

          {!gc ? (
            <div className="text-center py-16 text-[#86868B]">
              <Send className="h-10 w-10 mx-auto mb-3 opacity-30" strokeWidth={1.5} />
              <p className="text-sm">Wähle einen Deal und generiere deinen ersten Post.</p>
            </div>
          ) : (
            <div className="rounded-2xl bg-[#F5F5F7] p-5" data-testid="content-preview">
              {deal && <img src={result.image_url} alt="" className="w-full h-40 object-cover rounded-xl mb-4" />}
              <p className="font-heading font-bold text-lg text-[#1D1D1F] mb-2">{gc.headline}</p>
              <p className="text-sm text-[#1D1D1F] whitespace-pre-line mb-3">{gc.description}</p>
              <p className="text-sm font-semibold text-[#1D1D1F] mb-1">
                💶 Nur {result.deal_price.toFixed(2)} € statt <span className="line-through text-[#A1A1A6] font-normal">{result.original_price.toFixed(2)} €</span> (-{result.discount_percent}%)
              </p>
              <p className="text-sm font-medium text-[#0071E3] mb-3">👉 {gc.cta}</p>
              <p className="text-xs text-[#86868B] break-all mb-3">{result.affiliate_url}</p>
              <div className="flex flex-wrap gap-1.5">
                {gc.hashtags.map((h, i) => (
                  <span key={i} className="text-xs text-[#86868B] bg-white rounded-full px-2 py-0.5 border border-black/5">{h}</span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
